/**
 * Who this window is, and when the operator was last in it.
 *
 * Two facts that both layers of the telephone lease need and neither owns. The
 * local vote (`useDeviceLease.ts`) used to keep both as effect locals, which was
 * fine while it was the only voter - and stopped being fine when the server
 * became the second one, because the two must be talking about the same window
 * and the same moment or they can disagree about who holds the phone.
 *
 * ⚠ `claimedAt` IS AN EVENT STAMP, NOT A POLL. It moves when the window becomes
 * the active one - mounted, focused, brought back into view - and never merely
 * because the operator's cursor is in it. Sent to the server as "is focused
 * right now" it would flap: two machines left with the CRM in front both report
 * focus for ever, so each heartbeat would take the phone off the other, and
 * every handover destroys a Twilio `Device` and builds a new one.
 */

let id: string | null = null;

/**
 * Stable for the life of the tab, and generated rather than derived.
 *
 * Lazy so that importing this module does nothing - a test that pulls in the
 * lease rules should not be creating identities. Not a user id and not
 * anything an operator could recognise: it names a WINDOW, and two windows of
 * the same person are exactly the case being told apart.
 */
export function windowId(): string {
  if (!id) id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return id;
}

/**
 * Zero, not `Date.now()`, before anything has happened.
 *
 * A window that has never been active must lose the vote to one that has. Seeded
 * with the current time it would arrive claiming to be the most recently used
 * window in the account, and a background tab reloading itself would take the
 * telephone off the machine somebody is sitting at.
 */
let claimed = 0;

/** The operator is here now. Returns the stamp so a caller can post it. */
export function markWindowActive(): number {
  claimed = Date.now();
  return claimed;
}

/** When this window last became the active one; 0 if it never has. */
export function windowClaimedAt(): number {
  return claimed;
}
